import React, { useState } from 'react';
import styled from 'styled-components';
import { Trans } from '@lingui/react';
import { PaymentRythm, PaymentDisplay } from '@qover/domain';
import { SitemapMixin, Sitemap } from '@qover/typography';
import { useFeature } from '@qover/hooks/src';

const Container = styled.div`
  margin: 60px -20px 0;
  width: auto;
  @media (min-width: ${props => props.theme.breakpoints.minWidth.S}) {
    margin: 60px -24px 0;
  }
`;

const Tabs = styled.div`
  background-color: #fff;
  border-radius: 24px;
  border: 1px solid var(--color-text-quote-10);
  height: 40px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-area: '..';
  margin: 0 auto;
  position: absolute;
  padding: 2px;
  left: 0;
  right: 0;
  top: -20px;
  width: fit-content;
`;

const TabBackground = styled.div`
  --space-around: 4px;
  background-color: var(--color-secondary-100);
  border-radius: 20px;
  box-shadow: 0 2px 2px 0 var(--color-text-quote-20);
  height: calc(100% - 2 * var(--space-around));
  left: var(--space-around);
  position: absolute;
  top: var(--space-around);
  transition-duration: 200ms;
  transition-property: transform;
  transition-timing-function: cubic-bezier(0, 0, 0.2, 1);
  width: calc(50% - 2 * var(--space-around));
  z-index: 1;
`;

const TabItem = styled.button`
  ${SitemapMixin};
  align-items: center;
  background-color: transparent;
  border-radius: 20px;
  color: var(--color-secondary-100);
  display: flex;
  font-weight: bold;
  justify-content: center;
  padding: 4px 12px;
  outline: none;
  z-index: 2;
  transition-duration: 500ms;
  transition-property: color;
  transition-timing-function: cubic-bezier(0, 0, 0.2, 1);
  &.active {
    color: #fff;
  }
`;

const Background = styled.div`
  background-color: var(--color-text-quote-03);
  border: 1px solid var(--color-text-quote-10);
  margin-top: 20px;
  padding: 20px 24px 20px;
  position: relative;
  &.with-switch {
    padding-top: 40px;
  }
  @media (min-width: ${props => props.theme.breakpoints.minWidth.S}) {
    border-radius: ${props => props.theme.borders.cardRadius};
  }
`;

const messages = {
  [PaymentRythm.MONTHLY]: <Trans id="general.cart.perMonth" />,
  [PaymentRythm.ONE_TIME]: '',
  [PaymentRythm.YEARLY]: <Trans id="general.cart.perYear" />,
};

type InjectedProps = {
  paymentRythm: PaymentRythm;
  paymentRythmMessage: React.ReactNode;
};

type SummaryPriceDetailsProps = {
  showPaymentRythmSwitcher?: boolean;
  children(props: InjectedProps): JSX.Element;
  conditionsMessage?: React.ReactNode;
};

export function SummaryPriceDetails(props: SummaryPriceDetailsProps) {
  const cashback = useFeature<boolean>('cashback');
  const showDiscount = useFeature<boolean>('showDiscount');
  const paymentDisplay = useFeature<PaymentDisplay>('paymentDisplay');

  const [selected, setSelected] = useState(0);
  let paymentRythm = PaymentRythm.MONTHLY;

  if (!props.showPaymentRythmSwitcher) {
    paymentRythm = PaymentRythm.ONE_TIME;
  } else if (selected === 1) {
    paymentRythm = PaymentRythm.YEARLY;
  }

  if (paymentDisplay === PaymentDisplay.PAYMENT_DISPLAY_YEARLY) {
    paymentRythm = PaymentRythm.YEARLY;
  } else if (paymentDisplay === PaymentDisplay.PAYMENT_DISPLAY_MONTHLY) {
    paymentRythm = PaymentRythm.MONTHLY;
  }

  return (
    <Container>
      <Background
        className={props.showPaymentRythmSwitcher ? 'with-switch' : ''}
      >
        {props.showPaymentRythmSwitcher &&
          (!paymentDisplay ||
            paymentDisplay ===
              PaymentDisplay.PAYMENT_DISPLAY_MONTHLY_YEARLY) && (
            <Tabs>
              <TabBackground
                style={{
                  transform: `translateX(calc((${selected} * 100%) + ${selected} * 2 * var(--space-around)))`,
                }}
              />
              <TabItem
                className={selected === 0 ? 'active' : ''}
                type="button"
                onClick={() => setSelected(0)}
              >
                <Trans id="general.pricePerMonth" defaults="Prix par mois" />
              </TabItem>
              <TabItem
                className={selected === 1 ? 'active' : ''}
                type="button"
                onClick={() => setSelected(1)}
              >
                <Trans id="general.pricePerYear" defaults="Prix par an" />
              </TabItem>
            </Tabs>
          )}
        {props.children({
          paymentRythm,
          paymentRythmMessage: messages[paymentRythm],
        })}
      </Background>
      {(cashback || showDiscount) && props.conditionsMessage && (
        <div
          style={{
            margin: '20px 25px 0 0',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Sitemap as="div" data-test="general.summaryCard.total.conditions">
            {props.conditionsMessage}
          </Sitemap>
        </div>
      )}
    </Container>
  );
}
